import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { smartleadAPI } from '@/lib/smartlead-api'
import { DEMO_CAMPAIGNS, getDemoCampaignById, getDemoVariantsByCampaignId, getDemoAggregateMetrics } from '@/data/demoData'

export interface CampaignOperationalData {
  totalVariants: number
  activeVariants: number
  pausedVariants: number
  completedVariants: number
  leadsNotStarted: number
  runWay: number
  loading: boolean
}

export function useMultipleCampaignsOperationalData(campaigns: Array<{id: string, name: string, status: string, client_id: string}>): Record<string, CampaignOperationalData> {
  const [operationalData, setOperationalData] = useState<Record<string, CampaignOperationalData>>({})
  const [smartleadCampaigns, setSmartleadCampaigns] = useState<any[]>([])
  const [smartleadLoading, setSmartleadLoading] = useState(false)
  const [allSegments, setAllSegments] = useState<any[]>([])
  const [allVariants, setAllVariants] = useState<any[]>([])
  const [variantsLoading, setVariantsLoading] = useState(false)
  const [dailyAnalytics, setDailyAnalytics] = useState<any[]>([])
  const [analyticsLoading, setAnalyticsLoading] = useState(false)
  
  const fetchedCampaignIds = useRef<Set<string>>(new Set())
  const isInitialized = useRef(false)

  // Memoize campaign IDs to prevent unnecessary re-fetches
  const campaignIds = useMemo(() => 
    campaigns.map(c => c.id), 
    [campaigns]
  )

  // Fetch Smartlead campaigns when component mounts
  useEffect(() => {
    if (isInitialized.current) return
    
    const fetchSmartleadCampaigns = async () => {
      if (!smartleadAPI.isConfigured()) {
        // Smartlead API not configured, skip
        isInitialized.current = true
        return
      }

      try {
        setSmartleadLoading(true)
        const campaigns = await smartleadAPI.fetchCampaigns()
        setSmartleadCampaigns(campaigns)
      } catch (error) {
        setSmartleadCampaigns([])
      } finally {
        setSmartleadLoading(false)
        isInitialized.current = true
      }
    }

    fetchSmartleadCampaigns()
  }, [])

  // Fetch segments and variants for all campaigns in one batch
  const fetchCampaignData = useCallback(async (currentCampaignIds: string[]) => {
    if (currentCampaignIds.length === 0) return
    
    // Check if we already fetched data for these campaigns
    const newCampaignIds = currentCampaignIds.filter(id => !fetchedCampaignIds.current.has(id))
    if (newCampaignIds.length === 0) return

    try {
      setVariantsLoading(true)
      const { supabase } = await import('@/integrations/supabase/client')
      
      // Fetch segments for all campaigns at once (still needed for the
      // segment count shown in the Operational Overview; 0-segment
      // campaigns simply won't return rows here, which is correct).
      const { data: segments, error: segmentsError } = await supabase
        .from('ab_test_segments')
        .select('*')
        .in('campaign_id', currentCampaignIds)

      if (segmentsError) {
        console.error('Error fetching segments:', segmentsError)
        return
      }

      setAllSegments(prev => {
        const existingSegmentIds = new Set(prev.map(s => s.id))
        const newSegments = (segments || []).filter(s => !existingSegmentIds.has(s.id))
        return [...prev, ...newSegments]
      })

      // Phase M (2026-04-22): variants carry campaign_id directly, so we
      // fetch them by campaign_id instead of via segment_id. This fixes
      // Channel Status / Leads Not Started / Runway for 0-segment
      // campaigns (Duel V1, StoryStream V3+).
      const { data: variants, error: variantsError } = await supabase
        .from('ab_test_variants')
        .select('*')
        .in('campaign_id', currentCampaignIds)
        .neq('status', 'archived')

      if (variantsError) {
        console.error('Error fetching variants:', variantsError)
      } else {
        setAllVariants(prev => {
          const existingVariantIds = new Set(prev.map(v => v.id))
          const newVariants = (variants || []).filter(v => !existingVariantIds.has(v.id))
          return [...prev, ...newVariants]
        })
      }
      
      // Mark these campaigns as fetched
      newCampaignIds.forEach(id => fetchedCampaignIds.current.add(id))
      
    } catch (error) {
      console.error('Error fetching campaign data:', error)
    } finally {
      setVariantsLoading(false)
    }
  }, [])

  // Fetch daily analytics data.
  // Phase U (2026-04-23): fetch all rows then keep only the most recent
  // per smartlead_campaign_id. Paused/archived campaigns stop getting
  // fresh daily rows, but their queued-but-not-sent leads are still
  // real — pinning to today reported 0 on any paused campaign, hiding
  // the backlog. Matches the matview's leads_ops CTE so old/new UIs agree.
  const fetchDailyAnalytics = useCallback(async () => {
    try {
      setAnalyticsLoading(true)
      const { supabase } = await import('@/integrations/supabase/client')

      // Grab a reasonable time window (90 days) so we can pick the most
      // recent row per campaign_id in memory. 90 days × ~70 campaigns × ~1
      // row/day ≈ 6k rows — trivial.
      const since = new Date()
      since.setDate(since.getDate() - 90)
      const sinceStr = since.toISOString().split('T')[0]

      // Supabase / PostgREST enforces `max_rows` server-side (1000 by
      // default) regardless of client `.range()`. 90 days × ~70
      // campaigns ≈ 6k rows, so one request silently truncates to the
      // newest 1000 and paused campaigns (Feb-19 is their latest row)
      // never show up. Paginate in 1000-row pages until a short page.
      const PAGE = 1000
      const latestPerCampaign = new Map<string, any>()
      let start = 0
      let pages = 0
      let truncated = false
      while (true) {
        const { data, error } = await supabase
          .from('daily_campaign_analytics')
          .select('*')
          .gte('date', sinceStr)
          .order('date', { ascending: false })
          .order('campaign_id', { ascending: true })
          .range(start, start + PAGE - 1)
        if (error) {
          console.error('Error fetching daily analytics:', error)
          break
        }
        const rows = data || []
        for (const row of rows) {
          const cid = String(row.campaign_id)
          if (!latestPerCampaign.has(cid)) latestPerCampaign.set(cid, row)
        }
        pages += 1
        if (rows.length < PAGE) break
        start += PAGE
        if (pages > 50) { truncated = true; break } // safety: 50k rows max
      }
      if (truncated) console.warn('daily_campaign_analytics pagination stopped at 50k rows')
      setDailyAnalytics([...latestPerCampaign.values()])
    } catch (error) {
      console.error('Error fetching daily analytics:', error)
      setDailyAnalytics([])
    } finally {
      setAnalyticsLoading(false)
    }
  }, [])

  // Fetch data when campaigns change
  useEffect(() => {
    if (campaignIds.length > 0) {
      fetchCampaignData(campaignIds)
      fetchDailyAnalytics()
    }
  }, [campaignIds, fetchCampaignData, fetchDailyAnalytics])

  useEffect(() => {
    const calculateOperationalData = async () => {
      // Check if Smartlead is configured - if not, return demo data
      if (!smartleadAPI.isConfigured()) {
        // Smartlead API not configured, return demo data
        
        const campaignOperationalMap: Record<string, CampaignOperationalData> = {}
        
        campaigns.forEach(campaign => {
          // Get demo campaign data
          const demoCampaign = getDemoCampaignById(campaign.id)
          const demoVariants = getDemoVariantsByCampaignId(campaign.id)
          
          if (demoCampaign) {
            // Calculate demo operational data
            const totalVariants = demoVariants.length
            const activeVariants = demoVariants.filter(v => v.status === 'Active').length
            const pausedVariants = demoVariants.filter(v => v.status === 'Paused').length
            const completedVariants = demoVariants.filter(v => v.status === 'Completed').length
            
            // Demo lead calculations (realistic estimates)
            const leadsNotStarted = Math.floor(demoCampaign.total_leads * 0.15) // 15% not started
            const runWay = Math.floor(demoCampaign.total_leads * 0.25) // 25% run way
            
            campaignOperationalMap[campaign.id] = {
              totalVariants,
              activeVariants,
              pausedVariants,
              completedVariants,
              leadsNotStarted,
              runWay,
              loading: false
            }
          } else {
            // Fallback for campaigns not in demo data
            campaignOperationalMap[campaign.id] = {
              totalVariants: 0,
              activeVariants: 0,
              pausedVariants: 0,
              completedVariants: 0,
              leadsNotStarted: 0,
              runWay: 0,
              loading: false
            }
          }
        })
        
        setOperationalData(campaignOperationalMap)
        // Demo operational data loaded
        return
      }
      
      // FIXED: Reduced logging - only log when actually calculating (not on every render)
      // console.log('🔄 Calculating operational overview for multiple campaigns...')
      
      const campaignOperationalMap: Record<string, CampaignOperationalData> = {}

      campaigns.forEach(campaign => {
        // Get segments for this campaign (kept for downstream segment count
        // if anything reads it; not used for variant lookup anymore).
        const campaignSegments = allSegments.filter(s => s.campaign_id === campaign.id)

        // Phase M: match variants to campaign via campaign_id column so
        // 0-segment campaigns (Duel V1) surface all their variants.
        const campaignVariants = allVariants.filter(v =>
          v.campaign_id === campaign.id && v.status !== 'archived'
        )
        
        // Calculate variant status based on Smartlead campaign status when available
        let activeVariants = 0
        let pausedVariants = 0
        let completedVariants = 0

        campaignVariants.forEach(variant => {
          // Newer campaigns carry smartlead_campaign_id directly; legacy
          // rows only have it embedded in configuration.campaign_url.
          let extractedCampaignId: string | null = null
          if (variant.smartlead_campaign_id) {
            extractedCampaignId = String(variant.smartlead_campaign_id)
          } else if (variant.configuration?.campaign_url) {
            const urlMatch = variant.configuration.campaign_url.match(/\/email-campaign\/(\d+)\/?/)
            extractedCampaignId = urlMatch ? urlMatch[1] : null
          }

          const smartleadCampaign = smartleadCampaigns.find(sc =>
            sc.id?.toString() === extractedCampaignId
          )

          if (smartleadCampaign) {
            // Use Smartlead campaign status as source of truth
            switch (smartleadCampaign.status) {
              case 'ACTIVE':
                activeVariants++
                break
              case 'PAUSED':
                pausedVariants++
                break
              case 'COMPLETED':
                completedVariants++
                break
              case 'DRAFTED':
                // Count drafted as paused for operational purposes
                pausedVariants++
                break
              default:
                // Unknown status - use variant status as fallback
                if (variant.status === 'active') activeVariants++
                else if (variant.status === 'paused') pausedVariants++
                else if (variant.status === 'completed') completedVariants++
            }
          } else {
            // No Smartlead campaign found - use variant status
            if (variant.status === 'active') activeVariants++
            else if (variant.status === 'paused') pausedVariants++
            else if (variant.status === 'completed') completedVariants++
          }
        })

        // Calculate leads not started and runway from daily analytics
        let leadsNotStarted = 0
        let totalMaxLeadsPerDay = 0
        
        // Get all smartlead campaign IDs for this campaign's variants.
        // Newer campaigns (Campaign Model v2) populate the top-level
        // `smartlead_campaign_id` column directly; legacy rows only have
        // it inside `configuration.campaign_url`. Check both.
        const smartleadCampaignIds = new Set<string>()
        campaignVariants.forEach(variant => {
          if (variant.smartlead_campaign_id) {
            smartleadCampaignIds.add(String(variant.smartlead_campaign_id))
          } else if (variant.configuration?.campaign_url) {
            const urlMatch = variant.configuration.campaign_url.match(/\/email-campaign\/(\d+)\/?/)
            if (urlMatch) {
              smartleadCampaignIds.add(urlMatch[1])
            }
          }
        })
        
        // Sum up analytics data for all associated smartlead campaigns
        smartleadCampaignIds.forEach(smartleadId => {
          const analytics = dailyAnalytics.find(a => a.campaign_id === smartleadId)
          if (analytics) {
            leadsNotStarted += analytics.not_started_leads || 0
            totalMaxLeadsPerDay += analytics.max_leads_per_day || 0
          }
        })
        
        // Calculate runway (days)
        const runWay = totalMaxLeadsPerDay > 0 ? Math.ceil(leadsNotStarted / totalMaxLeadsPerDay) : 0

        campaignOperationalMap[campaign.id] = {
          totalVariants: campaignVariants.length,
          activeVariants,
          pausedVariants,
          completedVariants,
          leadsNotStarted,
          runWay,
          loading: false
        }
      })

      setOperationalData(campaignOperationalMap)
    }

    if (!variantsLoading && !smartleadLoading && !analyticsLoading && campaigns.length > 0) {
      calculateOperationalData()
    }
  }, [campaigns, allVariants, smartleadCampaigns, variantsLoading, smartleadLoading, analyticsLoading, allSegments, dailyAnalytics])

  return operationalData
}